// src/oura/components/onboarding/fields.tsx
//
// The form pieces the onboarding drawers and the New client dialog share:
// a labelled field with its error underneath, and the email fields (who it
// goes to, the subject, the body) that every message uses.

import { useId, useLayoutEffect, useRef, type ReactNode } from "react";
import { cn } from "../../lib/cn";
import { TYPE } from "@oura/ui";
import { inputClass, textareaClass, type MessageErrors, type MessageText } from "../../onboarding/messageText";

export function Field({
    label,
    hint,
    error,
    children,
    className,
}: {
    label: string;
    hint?: ReactNode;
    error?: string | null;
    /** Called with the id to put on the control and the id of its message. */
    children: (ids: { id: string; describedBy: string | undefined }) => ReactNode;
    className?: string;
}) {
    const id = useId();
    const noteId = `${id}-note`;
    const note = error || hint;
    return (
        <div className={cn("min-w-0", className)}>
            <label htmlFor={id} className={cn(TYPE.lab, "mb-1.5 block")}>{label}</label>
            {children({ id, describedBy: note ? noteId : undefined })}
            {note && (
                <p id={noteId} role={error ? "alert" : undefined} className={cn(TYPE.tc, "m-0 mt-1.5", error ? "font-medium text-rose-600" : "text-gray-500")}>
                    {note}
                </p>
            )}
        </div>
    );
}

/** A refusal from the server, shown where the staffer is standing. */
export function Refusal({ children, className }: { children: ReactNode; className?: string }) {
    return (
        <p role="alert" className={cn(TYPE.tb, "m-0 rounded-xl bg-rose-50 px-3 py-2.5 font-medium text-rose-700", className)}>
            {children}
        </p>
    );
}

export function MessageFields({
    value,
    onChange,
    errors,
    disabled,
    rows = 10,
    recipientsOnly = false,
}: {
    value: MessageText;
    onChange: (next: MessageText) => void;
    errors?: MessageErrors | null;
    disabled?: boolean;
    rows?: number;
    /** The kick-off is a template: who it goes to and who is copied, no subject or message. */
    recipientsOnly?: boolean;
}) {
    const set = (key: keyof MessageText) => (e: { target: { value: string } }) => onChange({ ...value, [key]: e.target.value });
    return (
        <div className="space-y-4">
            <Field label="To" error={errors?.to}>
                {({ id, describedBy }) => (
                    <input id={id} aria-describedby={describedBy} aria-invalid={!!errors?.to} className={inputClass(errors?.to)} value={value.to} onChange={set("to")} disabled={disabled} autoComplete="off" />
                )}
            </Field>
            <Field label="Copy to" error={errors?.cc}>
                {({ id, describedBy }) => (
                    <input id={id} aria-describedby={describedBy} aria-invalid={!!errors?.cc} className={inputClass(errors?.cc)} value={value.cc} onChange={set("cc")} disabled={disabled} autoComplete="off" placeholder="Nobody" />
                )}
            </Field>
            {!recipientsOnly && (
                <>
                    <Field label="Subject" error={errors?.subject}>
                        {({ id, describedBy }) => (
                            <input id={id} aria-describedby={describedBy} aria-invalid={!!errors?.subject} className={inputClass(errors?.subject)} value={value.subject} onChange={set("subject")} disabled={disabled} />
                        )}
                    </Field>
                    <Field label="Message" error={errors?.body}>
                        {({ id, describedBy }) => (
                            <GrowingTextarea id={id} describedBy={describedBy} invalid={!!errors?.body} className={textareaClass(errors?.body)} minRows={rows} value={value.body} onChange={set("body")} disabled={disabled} />
                        )}
                    </Field>
                </>
            )}
        </div>
    );
}

/** A message box that grows with its text, so the whole email is read before it is sent. */
function GrowingTextarea({ id, describedBy, invalid, className, minRows, value, onChange, disabled }: {
    id: string;
    describedBy: string | undefined;
    invalid: boolean;
    className: string;
    minRows: number;
    value: string;
    onChange: (e: { target: { value: string } }) => void;
    disabled?: boolean;
}) {
    const box = useRef<HTMLTextAreaElement>(null);
    useLayoutEffect(() => {
        const el = box.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight + 2}px`;
    }, [value]);
    return (
        <textarea
            ref={box}
            id={id}
            aria-describedby={describedBy}
            aria-invalid={invalid}
            className={`${className} resize-none overflow-hidden`}
            rows={Math.min(minRows, 6)}
            value={value}
            onChange={onChange}
            disabled={disabled}
        />
    );
}

/** Where every onboarding email comes from, said once under the fields. */
export function SenderLine() {
    return <p className={cn(TYPE.tc, "m-0 text-gray-500")}>From Angel, oura@ourteam.ai. Replies go to the same inbox.</p>;
}
